import type { FeatureCollection } from "geojson";
import type { Job } from "@domain/CRM/job";
import type { IJobs } from "@ports/jobs/IJobs";

export interface JobsDetailUseCasesDeps {
	jobsApi: IJobs;
	/** GeoJSON → shapefile `.zip` conversion (injected from `@domain/Geo/io` at
	 *  the composition root so the use case stays unit-testable without running
	 *  real SHP conversion). */
	toSHP: (fc: FeatureCollection, filename: string) => Promise<File>;
	/** A boundary as job prep's two derived fields: the "Bnd GeoJSON" file and the simplified "Bnd GeoJSON Text"
	 *  (injected from `@domain/Geo/io`, like `toSHP`). */
	toBoundaryGeoJSON: (fc: FeatureCollection) => { file: File; text: string };
}

/** The three geometry layers, mapped to their Airtable SHP attachment key. */
type GeometryLayer = "boundary" | "points" | "zones";
type AttachmentKey = keyof NonNullable<Job["attachments"]>;

/** The job's non-geometry files the detail view uploads as-is: its field notes, its lab check-in CSV and PDF, and
 *  its executed and intended mission KMLs. */
export type DocumentKey = "fieldNotes" | "labCSV" | "labPDF" | "exeMission" | "intMission";

const LAYER_TO_SHP_KEY: Record<GeometryLayer, "boundarySHP" | "pointsSHP" | "zonesSHP"> = {
	boundary: "boundarySHP",
	points: "pointsSHP",
	zones: "zonesSHP",
};

interface CropOptions {
	lastCrops: string[];
	nextCrops: string[];
}

/**
 * Read path for a single job's detail page + edit modal. Wraps load-by-id,
 * update, and the parallel fetch of crop autocomplete options.
 */
export interface JobsDetailUseCases {
	loadById(id: string, compressed?: boolean): Promise<Job | undefined>;
	update(job: Partial<Job> & { id?: string }): Promise<Job>;
	loadCropOptions(): Promise<CropOptions>;
	/** Convert a (normalized) FeatureCollection to a shapefile, upload it, set
	 *  the matching SHP attachment on the job, persist, and return the
	 *  server-canonical job (re-fetched so the map gets a fetchable URL). */
	uploadGeometry(job: Job, layer: GeometryLayer, fc: FeatureCollection, filename: string): Promise<Job | undefined>;
	/** Upload a raw file as one of the job's documents — appended to its field notes, which hold several;
	 *  replacing any other, which holds one — persist, and return the re-fetched job. */
	uploadDocument(job: Job, key: DocumentKey, file: File): Promise<Job | undefined>;
	/** Clear an attachment field (sets it to `[]`, which the backend persists as
	 *  "no files"). */
	clearAttachment(job: Job, key: AttachmentKey): Promise<Job | undefined>;
}

function loadById(deps: JobsDetailUseCasesDeps, id: string, compressed: boolean = false): Promise<Job | undefined> {
	return deps.jobsApi.getById(id, compressed);
}

function update(deps: JobsDetailUseCasesDeps, job: Partial<Job> & { id?: string }): Promise<Job> {
	return deps.jobsApi.updateOne(job);
}

/**
 * Fetch both crop option lists in parallel. Editing the detail form opens
 * an autocomplete for `lastCrop` and `nextCrop`; we want both populated
 * before the user clicks into either.
 */
async function loadCropOptions(deps: JobsDetailUseCasesDeps): Promise<CropOptions> {
	const [lastCrops, nextCrops] = await Promise.all([
		deps.jobsApi.getLastCropOptions(),
		deps.jobsApi.getNextCropOptions(),
	]);
	return { lastCrops, nextCrops };
}

/**
 * The shapefile is a zip, so it is named as one — as job submission names its own (`<id>_<layer>.zip`). The upload
 * keeps whatever name the file carries: left bare, the S3 key and the Airtable attachment have no extension either.
 */
function zipFilename(filename: string): string {
	return /\.zip$/i.test(filename) ? filename : `${filename}.zip`;
}

/**
 * Convert `fc` to a shapefile, upload it, attach the result under the layer's
 * SHP key, persist, and return the re-fetched (server-canonical) job. Callers
 * pass an already-normalized FeatureCollection (points get sample-id handling
 * upstream in the view model).
 *
 * A boundary also replaces what job prep derives from the shapefile — the "Bnd GeoJSON" attachment and the
 * "Bnd GeoJSON Text" — since Mission Control and the portal's map read those before "Bnd Shp": left stale,
 * the saved boundary would never show.
 */
async function uploadGeometry(
	deps: JobsDetailUseCasesDeps,
	job: Job,
	layer: GeometryLayer,
	fc: FeatureCollection,
	filename: string,
): Promise<Job | undefined> {
	const shp = await deps.toSHP(fc, zipFilename(filename));
	const attachment = await deps.jobsApi.uploadFile(job.id, shp);
	const attachments = { ...(job.attachments ?? {}), [LAYER_TO_SHP_KEY[layer]]: [attachment] };
	let updated: Job = { ...job, attachments } as Job;
	if (layer === "boundary") {
		const { file, text } = deps.toBoundaryGeoJSON(fc);
		const geoJsonAttachment = await deps.jobsApi.uploadFile(job.id, file);
		updated = {
			...updated,
			attachments: { ...attachments, boundaryGeoJSON: [geoJsonAttachment] },
			boundaryGeoJsonText: text,
		} as Job;
	}
	await deps.jobsApi.updateOne(updated);
	return loadById(deps, job.id, false);
}

/** Upload a raw file and append it to the job's `fieldNotes`, then persist. */
async function uploadDocument(
	deps: JobsDetailUseCasesDeps,
	job: Job,
	key: DocumentKey,
	file: File,
): Promise<Job | undefined> {
	const attachment = await deps.jobsApi.uploadFile(job.id, file);
	const kept = key === "fieldNotes" ? (job.attachments?.fieldNotes ?? []) : [];
	const updated = {
		...job,
		attachments: { ...(job.attachments ?? {}), [key]: [...kept, attachment] },
	};
	await deps.jobsApi.updateOne(updated);
	return loadById(deps, job.id, false);
}

/** Clear an attachment field by setting it to `[]`, then persist. */
async function clearAttachment(deps: JobsDetailUseCasesDeps, job: Job, key: AttachmentKey): Promise<Job | undefined> {
	const updated = { ...job, attachments: { ...(job.attachments ?? {}), [key]: [] } };
	await deps.jobsApi.updateOne(updated);
	return loadById(deps, job.id, false);
}

export function makeJobsDetailUseCases(deps: JobsDetailUseCasesDeps): JobsDetailUseCases {
	return {
		loadById: (id, compressed) => loadById(deps, id, compressed),
		update: (job) => update(deps, job),
		loadCropOptions: () => loadCropOptions(deps),
		uploadGeometry: (job, layer, fc, filename) => uploadGeometry(deps, job, layer, fc, filename),
		uploadDocument: (job, key, file) => uploadDocument(deps, job, key, file),
		clearAttachment: (job, key) => clearAttachment(deps, job, key),
	};
}
